import React, { createContext, useContext } from "react";
import { useWindowDimensions } from "react-native";

/**
 * The one size rule every v2 view lays out by (NEW_ORDER_V2_PLAN.md rule 13, N26): compact is a
 * window 768 wide or narrower. A library hook (COMPONENT_LIBRARY.md §4), promoted from My Orders'
 * `useCompact` in L0 (2026-10-06), which re-exports it.
 *
 * It asks for a narrow WINDOW, not a particular app: mobile runs on phones AND on landscape POS
 * tablets, and web can be narrow. 768 is the repo's one breakpoint literal
 * (`commonHelpers.isMobileView`), reused rather than invented, but not that function, which answers
 * `true` on every native device and does not re-render on resize. A width of 0 is the node harness
 * with no DOM, where "unknown" must mean wide.
 */
export const COMPACT_MAX_WIDTH = 768;

/** An answer forced by `CompactOverride`, or null to ask the window. */
const CompactOverrideContext = createContext<boolean | null>(null);

/**
 * Makes `useCompact()` answer `compact` for everything inside, whatever the window: the design gallery draws a
 * view in a phone-wide frame and a wide one on the same page. Nothing in an app mounts it.
 */
export function CompactOverride({ compact, children }: { compact: boolean; children: React.ReactNode }) {
  return React.createElement(CompactOverrideContext.Provider, { value: compact }, children);
}

export function useCompact(): boolean {
  const { width } = useWindowDimensions();
  const forced = useContext(CompactOverrideContext);
  if (forced !== null) return forced;
  return width > 0 && width <= COMPACT_MAX_WIDTH;
}
